import { isPaymentEffectivelyPaid } from "./rent";

/**
 * Canonical effective payment status.
 *
 * There is exactly ONE rule for "is this payment actually settled", and it lives
 * in `isPaymentEffectivelyPaid` (@/lib/rent). This module is the display-facing
 * wrapper around it — it turns that boolean into the status string a badge
 * renders. It must never re-implement the rule.
 *
 * The rule (see rent.ts for the full reasoning):
 *   • Stripe rows trust payments.status — the gateway is authoritative.
 *   • Manual rows need CORROBORATION: an approved proof, or `status = 'paid'`
 *     backed by either a proof on file or an ADMIN ATTESTATION
 *     (`marked_paid_at` / `marked_paid_by`, written by the "Mark paid" actions).
 *   • A bare `status = 'paid'` with nothing behind it is somebody's word for it
 *     and is demoted back to "pending".
 *
 * WHY THIS FILE DELEGATES (#1326): it used to carry its own, older copy of the
 * rule — "manual counts as paid ONLY when the proof is approved" — written
 * before the attestation columns existed. Admin surfaces moved on to
 * `isPaymentEffectivelyPaid` and the tenant Dashboard stayed here, so an admin
 * "Mark deposit paid" (which writes the attestation but does NOT approve the
 * proof) read as Paid in the admin tenant profile and Deposit pending on the
 * tenant's own dashboard. Two definitions of "paid" is the bug; keep one.
 *
 * Every caller must therefore pass the corroboration it has — build the input
 * with `paymentStatusInput(payment, proof)` rather than by hand, and make sure
 * the query behind it selects `marked_paid_at` / `marked_paid_by`. A row read
 * without those columns cannot be recognised as admin-attested.
 */

export type EffectiveStatusInput = {
  /** Raw payments.status. */
  status?: string | null;
  /** True when the payment was settled through Stripe (trust the gateway). */
  isStripe?: boolean;
  /** Status of the latest payment_proofs row for this payment, if any. */
  proofStatus?: string | null;
  /**
   * Does a payment_proofs row exist at all (any status)? Defaults to
   * "proofStatus was supplied", which is right for callers that only ever have
   * the latest proof's status to hand.
   */
  hasProof?: boolean;
  /** payments.marked_paid_at — half of the admin attestation. */
  markedPaidAt?: string | null;
  /** payments.marked_paid_by — the other half. */
  markedPaidBy?: string | null;
  /** payments.amount_cents — what was billed (COL-417). */
  amountCents?: number | null;
  /** payments.received_cents — what actually arrived (COL-417). */
  receivedCents?: number | null;
};

/** Whether a manual proof has been approved. */
export function isProofApproved(proofStatus?: string | null): boolean {
  return String(proofStatus || "").toLowerCase() === "approved";
}

/**
 * Build the canonical input from the rows a screen already has, so no caller
 * has to remember which corroboration fields exist. Pass `isStripe` only when
 * the screen decides that some other way (e.g. from a payment-method label);
 * otherwise it is read off the row's Stripe ids.
 */
export function paymentStatusInput(
  payment:
    | {
        id?: string | number | null;
        status?: string | null;
        stripe_payment_intent_id?: string | null;
        stripe_charge_id?: string | null;
        marked_paid_at?: string | null;
        marked_paid_by?: string | null;
        amount_cents?: number | null;
        received_cents?: number | null;
      }
    | null
    | undefined,
  proof: { status?: string | null } | null | undefined,
  opts?: { isStripe?: boolean }
): EffectiveStatusInput {
  return {
    status: payment?.status ?? null,
    isStripe:
      opts?.isStripe ??
      !!(payment?.stripe_payment_intent_id || payment?.stripe_charge_id),
    proofStatus: proof?.status ?? null,
    hasProof: !!proof,
    markedPaidAt: payment?.marked_paid_at ?? null,
    markedPaidBy: payment?.marked_paid_by ?? null,
    amountCents: payment?.amount_cents ?? null,
    receivedCents: payment?.received_cents ?? null,
  };
}

/** Resolve the status a payment should actually display / be counted as. */
export function effectivePaymentStatus(input: EffectiveStatusInput): string {
  const status = input.status || "pending";
  if (input.isStripe) return status;
  // Terminal states are authoritative — a canceled/failed row must never be
  // promoted to "paid" just because a proof got approved (mirrors main's
  // getEffectivePaymentStatus, which trusts the raw status here).
  if (status === "canceled" || status === "failed") return status;

  const hasProof = input.hasProof ?? input.proofStatus != null;
  // COL-417: the amounts travel with the synthetic row, or only the
  // status half of the amount gate would survive this path — a row left
  // 'paid' by another surface while the ledger shows a shortfall would read
  // as settled here while every SQL consumer called it open.
  const settled = isPaymentEffectivelyPaid(
    {
      id: "effective-status",
      status,
      marked_paid_at: input.markedPaidAt ?? null,
      marked_paid_by: input.markedPaidBy ?? null,
      amount_cents: input.amountCents ?? null,
      received_cents: input.receivedCents ?? null,
    },
    () => (hasProof ? { status: input.proofStatus ?? null } : null)
  );
  if (settled) return "paid";
  // Manual, marked paid but nothing corroborates it → still pending review.
  if (status === "paid") return "pending";
  return status;
}

/** Convenience predicate. */
export function isEffectivelyPaid(input: EffectiveStatusInput): boolean {
  return effectivePaymentStatus(input) === "paid";
}
